// ── Design decisions ──
// Recibos, foto del paquete y "Analizar recibo con IA": exactamente lo que hoy
// vive al pie de PackageCard, por paquete y sin cambios de comportamiento
// (máximo 20 recibos, 5 MB por archivo, el último recibo queda como
// receipt_url legado). La diferencia es que las escrituras van por el
// adaptador (api.subirRecibo / api.contarRecibos / api.subirFotoPaquete) y
// que los errores se muestran en línea, nunca con alert().

import { useEffect, useState } from 'react';
import { CheckCircle, ExternalLink } from 'lucide-react';

const LIMITE_RECIBOS = 20;

const PaqueteAdjuntos = ({ paquete, encomiendaPublicId, api, onAnalyzeReceipt }) => {
  const pkgId = paquete?.id;
  const [receiptUrl, setReceiptUrl] = useState(paquete?.receiptUrl || null);
  const [receiptCount, setReceiptCount] = useState(0);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptError, setReceiptError] = useState('');
  const [photoUrl, setPhotoUrl] = useState(paquete?.photoUrl || null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    if (!pkgId || typeof api?.contarRecibos !== 'function') return undefined;
    let cancelado = false;
    api
      .contarRecibos(pkgId)
      .then((n) => {
        if (!cancelado) setReceiptCount(Number(n) || 0);
      })
      .catch(() => {});
    return () => {
      cancelado = true;
    };
  }, [pkgId, api]);

  const handleReceiptUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (receiptCount >= LIMITE_RECIBOS) {
      setReceiptError('Máximo 20 recibos por paquete.');
      e.target.value = '';
      return;
    }
    setReceiptError('');
    setReceiptUploading(true);
    try {
      const r = await api.subirRecibo({ pkgId, encomiendaPublicId, files, receiptCount });
      if (r?.lastUrl) setReceiptUrl(r.lastUrl);
      if (typeof r?.count === 'number') setReceiptCount(r.count);
      if (Array.isArray(r?.errores) && r.errores.length > 0) setReceiptError(r.errores.join(' · '));
    } catch (err) {
      setReceiptError(err?.message || 'No se pudo subir el recibo. Intentá de nuevo.');
    } finally {
      setReceiptUploading(false);
      e.target.value = '';
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('La foto no puede exceder 5MB.');
      return;
    }
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const url = await api.subirFotoPaquete({
        pkgId,
        encomiendaPublicId,
        packageNumber: paquete?.package_number,
        file,
      });
      if (url) setPhotoUrl(url);
    } catch (err) {
      setPhotoError(err?.message || 'No se pudo subir la foto. Intentá de nuevo.');
    } finally {
      setPhotoUploading(false);
    }
  };

  if (!pkgId) return null;

  return (
    <div className="pt-2 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 flex-wrap">
        {receiptCount > 0 ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="badge badge-done flex items-center gap-1">
              <CheckCircle size={12} aria-hidden="true" /> {receiptCount} recibo(s)
            </span>
            {receiptUrl && (
              <a
                href={receiptUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Ver último recibo"
                className="btn btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                style={{ color: 'var(--blue-text)' }}
              >
                <ExternalLink size={11} aria-hidden="true" /> Ver último
              </a>
            )}
            {receiptCount < LIMITE_RECIBOS && (
              <label className="btn btn-ghost focus-within:ring-2 focus-within:ring-[var(--amber)]" style={{ color: 'var(--amber)', cursor: 'pointer' }}>
                + Agregar recibo
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/jpeg,image/png,application/pdf"
                  aria-label="Agregar recibo"
                  onChange={handleReceiptUpload}
                  disabled={receiptUploading}
                />
              </label>
            )}
          </div>
        ) : (
          <label
            className="btn btn-secondary focus-within:ring-2 focus-within:ring-[var(--avianca-red)]"
            style={{ color: 'var(--avianca-red)', borderColor: 'var(--avianca-red)', cursor: 'pointer' }}
          >
            {receiptUploading ? 'Subiendo…' : '📎 Subir recibo'}
            <input
              type="file"
              className="hidden"
              multiple
              accept="image/jpeg,image/png,application/pdf"
              aria-label="Subir recibo"
              onChange={handleReceiptUpload}
              disabled={receiptUploading}
            />
          </label>
        )}
        {onAnalyzeReceipt && (
          <button
            type="button"
            onClick={() => onAnalyzeReceipt(pkgId)}
            aria-label="Analizar recibo con IA"
            className="btn btn-secondary focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            style={{ color: 'var(--blue-text)', borderColor: 'var(--blue)' }}
          >
            🔍 Analizar recibo con IA
          </button>
        )}
      </div>
      {receiptError && (
        <p role="alert" className="text-[10px]" style={{ color: 'var(--avianca-red)' }}>
          {receiptError}
        </p>
      )}

      <div>
        {photoUrl ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>📷 Foto del paquete:</span>
            <a
              href={photoUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Ver foto del paquete"
              style={{ fontSize: '11px', color: 'var(--blue-text)', textDecoration: 'underline' }}
            >
              Ver foto
            </a>
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'underline' }}>
              Cambiar
              <input
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                aria-label="Cambiar foto del paquete"
                onChange={(e) => e.target.files[0] && handlePhotoUpload(e.target.files[0])}
              />
            </label>
          </div>
        ) : (
          <label
            className="focus-within:ring-2 focus-within:ring-[var(--blue)]"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'var(--blue-text)',
              background: 'var(--tile)',
            }}
          >
            {photoUploading ? 'Subiendo…' : '📷 Adjuntar foto del paquete'}
            <input
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              aria-label="Adjuntar foto del paquete"
              onChange={(e) => e.target.files[0] && handlePhotoUpload(e.target.files[0])}
              disabled={photoUploading}
            />
          </label>
        )}
        {photoError && (
          <p role="alert" className="text-[10px] mt-1" style={{ color: 'var(--avianca-red)' }}>
            {photoError}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaqueteAdjuntos;
